/**
 * Facility Service
 * Handles all facility-related API operations (CRUD)
 */

import { API_ENDPOINTS } from '../config/api.config';
import { apiGet, apiDelete, ApiError, apiRequest } from '../utils/apiClient';
import { mapFormValuesToFormData } from '../utils/facilityMapper';
import type {
  Facility,
  FacilityCreateRequest,
  FacilityListResponse,
  FacilityResponse,
  PaginationInfo,
} from '../types/facility.types';
import type { FacilityFormValues } from '../utils/facilityMapper';

export interface FacilityServiceResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  pagination?: PaginationInfo;
}

function buildFacilityListQueryParams(
  page: number,
  filters?: {
    search?: string;
    facility_code?: string;
    facility_name?: string;
    facility_email?: string;
    admin_name?: string;
  }
): string[] {
  const queryParams: string[] = [];
  if (page) {
    queryParams.push(`page=${page}`);
  }
  if (!filters) {
    return queryParams;
  }
  if (filters.search?.trim()) {
    queryParams.push(`search=${encodeURIComponent(filters.search.trim())}`);
  }
  if (filters.facility_code) {
    queryParams.push(`facility_code=${encodeURIComponent(filters.facility_code)}`);
  }
  if (filters.facility_name) {
    queryParams.push(`facility_name=${encodeURIComponent(filters.facility_name)}`);
  }
  if (filters.facility_email) {
    queryParams.push(`facility_email=${encodeURIComponent(filters.facility_email)}`);
  }
  if (filters.admin_name) {
    queryParams.push(`admin_name=${encodeURIComponent(filters.admin_name)}`);
  }
  return queryParams;
}

function parseFacilitiesListPayload(
  responseData: unknown
): FacilityServiceResponse<Facility[]> | null {
  if (!responseData || typeof responseData !== 'object') {
    return null;
  }
  const apiResponse = responseData as FacilityListResponse;

  if (apiResponse.success && apiResponse.data) {
    return {
      success: true,
      data: apiResponse.data,
      pagination: apiResponse.pagination,
      message: apiResponse.message,
    };
  }
  return {
    success: false,
    error: apiResponse.message || 'Failed to fetch facilities',
    data: [],
  };
}

function facilitiesFromLegacyResponse(responseData: unknown): Facility[] {
  if (Array.isArray(responseData)) {
    return responseData;
  }
  if (responseData && typeof responseData === 'object' && 'data' in responseData) {
    const nested = responseData as { data?: Facility[] };
    return nested.data || [];
  }
  return [];
}

type FacilityErrorResponse = {
  success?: boolean;
  errors?: Array<{ msg?: string; message?: string; path?: string }>;
  message?: string;
  error?: string | { errorMessage?: string };
};

function messageFromFacilityErrorResponse(errorResponse: FacilityErrorResponse): string | null {
  if (
    errorResponse.errors &&
    Array.isArray(errorResponse.errors) &&
    errorResponse.errors.length > 0
  ) {
    const errorMessages = errorResponse.errors
      .map(err => err.msg || err.message || 'Validation error')
      .filter(Boolean);
    return errorMessages.join('; ');
  }

  if (
    errorResponse.error &&
    typeof errorResponse.error === 'object' &&
    errorResponse.error !== null
  ) {
    const errorObj = errorResponse.error as { errorMessage?: string };
    if (errorObj.errorMessage) {
      return errorObj.errorMessage;
    }
  }

  if (errorResponse.message) {
    return errorResponse.message;
  }
  if (errorResponse.error && typeof errorResponse.error === 'string') {
    return errorResponse.error;
  }
  return null;
}

class FacilityService {
  /**
   * Get all facilities with pagination and filters
   * Uses GET /ris/api/facilities/ (without ID)
   * @param page Optional page number (default: 1)
   * @param filters Optional filter parameters (search, facility_code, facility_name, facility_email, admin_name)
   */
  async getAllFacilities(
    page: number = 1,
    filters?: {
      search?: string;
      facility_code?: string;
      facility_name?: string;
      facility_email?: string;
      admin_name?: string;
    }
  ): Promise<FacilityServiceResponse<Facility[]>> {
    try {
      const queryParams = buildFacilityListQueryParams(page, filters);
      const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
      const url = `${API_ENDPOINTS.FACILITIES.GET()}${queryString}`;
      const response = await apiGet<FacilityListResponse>(url);

      const parsed = parseFacilitiesListPayload(response.data);
      if (parsed) {
        return parsed;
      }

      // Fallback for old format (backward compatibility)
      return {
        success: true,
        data: facilitiesFromLegacyResponse(response.data),
      };
    } catch (error) {
      console.error('Error fetching facilities:', error);
      return {
        success: false,
        error: this.extractErrorMessage(error),
        data: [],
      };
    }
  }

  /**
   * Get a single facility by ID
   * Uses GET /ris/api/facilities/{id} (with ID)
   */
  async getFacilityById(id: number | string): Promise<FacilityServiceResponse<Facility>> {
    try {
      const response = await apiGet<
        { success?: boolean; message?: string; data?: Facility } | FacilityResponse | Facility
      >(
        API_ENDPOINTS.FACILITIES.GET(id) // With ID parameter
      );

      // Handle different response formats
      let facility: Facility;

      // Check if response has success wrapper: { success: true, message: "...", data: {...} }
      if (
        response.data &&
        typeof response.data === 'object' &&
        'success' in response.data &&
        'data' in response.data
      ) {
        const wrappedResponse = response.data as {
          success?: boolean;
          message?: string;
          data?: Facility;
        };
        if (wrappedResponse.success && wrappedResponse.data) {
          facility = wrappedResponse.data;
        } else {
          throw new Error(wrappedResponse.message || 'Failed to fetch facility');
        }
      } else if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        // Handle FacilityResponse format: { data: Facility }
        facility = (response.data as FacilityResponse).data;
      } else {
        // Handle direct Facility format
        facility = response.data as Facility;
      }

      return {
        success: true,
        data: facility,
      };
    } catch (error) {
      console.error('Error fetching facility:', error);
      return {
        success: false,
        error: this.extractErrorMessage(error),
      };
    }
  }

  /**
   * List facilities filtered by type (e.g. HUB for Spoke linking).
   */
  async getFacilitiesByType(
    facilityType: string,
    page: number = 1,
    limit: number = 50
  ): Promise<FacilityServiceResponse<Facility[]>> {
    try {
      const queryParams = [
        `facility_type=${encodeURIComponent(facilityType)}`,
        `page=${page}`,
        `limit=${limit}`,
      ];
      const url = `${API_ENDPOINTS.FACILITIES.GET()}?${queryParams.join('&')}`;
      const response = await apiGet<FacilityListResponse>(url);

      if (response.data && typeof response.data === 'object') {
        const apiResponse = response.data as FacilityListResponse;
        if (apiResponse.success && apiResponse.data) {
          return {
            success: true,
            data: apiResponse.data,
            pagination: apiResponse.pagination,
            message: apiResponse.message,
          };
        }
        return {
          success: false,
          error: apiResponse.message || 'Failed to fetch facilities',
          data: [],
        };
      }

      let facilities: Facility[] = [];
      if (Array.isArray(response.data)) {
        facilities = response.data;
      }

      return {
        success: true,
        data: facilities,
      };
    } catch (error) {
      console.error('Error fetching facilities by type:', error);
      return {
        success: false,
        error: this.extractErrorMessage(error),
        data: [],
      };
    }
  }

  /**
   * Search facilities for filter dropdown options
   * Uses GET /ris/api/facilities/ with search parameter for fuzzy matching
   * @param field - The field to search (facility_code, facility_name, facility_email, admin_name)
   * @param searchQuery - The search query string
   * @returns Array of unique option values for the specified field
   */
  async searchFacilityOptions(
    field: 'facility_code' | 'facility_name' | 'facility_email' | 'admin_name',
    searchQuery: string
  ): Promise<{ value: string; label: string }[]> {
    try {
      const queryParams: string[] = [];
      queryParams.push('page=1'); // Always use page 1 for search

      // Add search query for the specific field
      if (searchQuery.trim()) {
        queryParams.push(`${field}=${encodeURIComponent(searchQuery)}`);
      }

      const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
      const url = `${API_ENDPOINTS.FACILITIES.GET()}${queryString}`;
      const response = await apiGet<FacilityListResponse>(url);

      let facilities: Facility[] = [];

      // Handle response format
      if (response.data && typeof response.data === 'object') {
        const apiResponse = response.data as FacilityListResponse;
        if (apiResponse.success && apiResponse.data && Array.isArray(apiResponse.data)) {
          facilities = apiResponse.data;
        } else if (Array.isArray(response.data)) {
          facilities = response.data;
        } else if ('data' in apiResponse && Array.isArray(apiResponse.data)) {
          // Handle case where data might be nested differently
          facilities = apiResponse.data;
        }
      }

      // Extract unique values for the specified field
      const uniqueValues = new Set<string>();
      const options: { value: string; label: string }[] = [];

      facilities.forEach(facility => {
        let fieldValue: string | undefined;

        switch (field) {
          case 'facility_code':
            fieldValue = facility.facility_code;
            break;
          case 'facility_name':
            fieldValue = facility.facility_name;
            break;
          case 'facility_email':
            fieldValue = facility.email_id;
            break;
          case 'admin_name':
            fieldValue = facility.users?.full_name;
            break;
        }

        if (fieldValue && !uniqueValues.has(fieldValue)) {
          uniqueValues.add(fieldValue);
          options.push({ value: fieldValue, label: fieldValue });
        }
      });

      // Debug logging to help diagnose issues
      if (options.length === 0 && facilities.length > 0) {
        console.warn(
          `No options extracted for field "${field}" from ${facilities.length} facilities:`,
          {
            field,
            searchQuery,
            sampleFacility: facilities[0],
          }
        );
      }

      return options;
    } catch (error) {
      console.error(`Error searching facility options for ${field}:`, error);
      return [];
    }
  }

  /**
   * Extract error message from API error response
   */
  private extractErrorMessage(error: unknown): string {
    if (!(error instanceof ApiError)) {
      return error instanceof Error ? error.message : 'An unexpected error occurred';
    }
    if (error.response && typeof error.response === 'object') {
      const fromResponse = messageFromFacilityErrorResponse(
        error.response as FacilityErrorResponse
      );
      if (fromResponse) {
        return fromResponse;
      }
    }
    return error.message;
  }

  /**
   * Create a new facility
   * Uses FormData for multipart/form-data upload (supports file uploads like logo)
   */
  async createFacility(
    facilityData: FacilityFormValues
  ): Promise<FacilityServiceResponse<Facility>> {
    try {
      // Convert form values to FormData for multipart/form-data upload
      const formData = mapFormValuesToFormData(facilityData);

      // Use apiRequest directly with FormData (don't set Content-Type, let browser set it with boundary)
      const response = await apiRequest<FacilityResponse | Facility>(
        API_ENDPOINTS.FACILITIES.CREATE,
        {
          method: 'POST',
          body: formData,
          headers: {
            // Don't set Content-Type header - browser will set it with boundary for FormData
          },
        }
      );

      // Check if response indicates failure
      if (response.data && typeof response.data === 'object') {
        const apiResponse = response.data as {
          success?: boolean;
          message?: string;
          error?: string | { errorMessage?: string };
        };
        if (apiResponse.success === false) {
          // Handle error response - pass full response data to extractErrorMessage
          const errorMessage = this.extractErrorMessage({
            response: apiResponse,
            message: apiResponse.message || 'Failed to create facility',
          } as ApiError);
          return {
            success: false,
            error: errorMessage,
          };
        }
      }

      // Handle different response formats
      let facility: Facility;
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        facility = (response.data as FacilityResponse).data;
      } else {
        facility = response.data as Facility;
      }

      return {
        success: true,
        data: facility,
        message: 'Facility created successfully',
      };
    } catch (error) {
      console.error('Error creating facility:', error);
      return {
        success: false,
        error: this.extractErrorMessage(error),
      };
    }
  }

  /**
   * Update an existing facility
   * Always uses FormData for multipart/form-data upload (supports file uploads like logo)
   * Accepts either FacilityFormValues (for full updates with files) or Partial<FacilityCreateRequest> (for simple updates)
   */
  async updateFacility(
    id: number | string,
    facilityData: FacilityFormValues | Partial<FacilityCreateRequest>
  ): Promise<FacilityServiceResponse<Facility>> {
    try {
      // Check if facilityData is FormValues (has all form fields) or plain object
      const isFormValues =
        facilityData &&
        typeof facilityData === 'object' &&
        'name' in facilityData &&
        'type' in facilityData;

      let formData: FormData;
      if (isFormValues) {
        // Convert form values to FormData for multipart/form-data upload
        formData = mapFormValuesToFormData(facilityData as FacilityFormValues);
      } else {
        // Convert plain object to FormData for consistency
        const plainData = facilityData as Partial<FacilityCreateRequest>;
        formData = new FormData();

        // Append all fields from the plain object to FormData
        Object.entries(plainData).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            // Convert value to string for FormData (handles numbers, booleans, etc.)
            // FacilityCreateRequest doesn't include File/Blob types, so we can safely convert to string
            formData.append(key, String(value));
          }
        });
      }

      // Always use FormData for updates
      const response = await apiRequest<FacilityResponse | Facility>(
        API_ENDPOINTS.FACILITIES.UPDATE(id),
        {
          method: 'PUT',
          body: formData,
          headers: {
            // Don't set Content-Type header - browser will set it with boundary for FormData
          },
        }
      );

      // Check if response indicates failure
      if (response.data && typeof response.data === 'object') {
        const apiResponse = response.data as {
          success?: boolean;
          message?: string;
          error?: string | { errorMessage?: string };
        };
        if (apiResponse.success === false) {
          // Handle error response - pass full response data to extractErrorMessage
          const errorMessage = this.extractErrorMessage({
            response: apiResponse,
            message: apiResponse.message || 'Failed to update facility',
          } as ApiError);
          return {
            success: false,
            error: errorMessage,
          };
        }
      }

      // Handle different response formats
      let facility: Facility;
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        facility = (response.data as FacilityResponse).data;
      } else {
        facility = response.data as Facility;
      }

      return {
        success: true,
        data: facility,
        message: 'Facility updated successfully',
      };
    } catch (error) {
      console.error('Error updating facility:', error);
      return {
        success: false,
        error: this.extractErrorMessage(error),
      };
    }
  }

  /**
   * Delete a facility
   */
  async deleteFacility(id: number | string): Promise<FacilityServiceResponse<void>> {
    try {
      await apiDelete<void>(API_ENDPOINTS.FACILITIES.DELETE(id));

      return {
        success: true,
        message: 'Facility deleted successfully',
      };
    } catch (error) {
      console.error('Error deleting facility:', error);
      return {
        success: false,
        error: this.extractErrorMessage(error),
      };
    }
  }
}

// Export singleton instance
export const facilityService = new FacilityService();
export default facilityService;
